import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { CheckCircle2, Copy, X } from 'lucide-react';
import { createPortal } from 'react-dom';

import type { HighRiskTransaction } from './types';

type TransactionDetailModalProps = {
  transaction: HighRiskTransaction | null;
  isOpen: boolean;
  onClose: () => void;
};

const getLabelColor = (label: string) => {
  switch (label) {
    case 'micro_test':
      return 'bg-purple-100 text-purple-700 border-purple-300';
    case 'duplicate':
      return 'bg-orange-100 text-orange-700 border-orange-300';
    case 'refund_cluster':
      return 'bg-blue-100 text-blue-700 border-blue-300';
    case 'tier_cluster':
      return 'bg-red-100 text-red-700 border-red-300';
    case 'off_catalogue':
      return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-300';
  }
};

const formatLabel = (label: string) => {
  return label
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const TransactionDetailModal: FunctionComponent<TransactionDetailModalProps> = ({
  transaction,
  isOpen,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen || !transaction) return null;

  const handleCopyReferences = async () => {
    const references = transaction.reference_numbers.join('\n');
    await navigator.clipboard.writeText(references);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed left-0 top-0 z-50 flex h-screen w-screen items-center justify-center bg-black/50 backdrop-blur-sm transition-opacity duration-300"
      onClick={handleBackdropClick}
    >
      <div className="animate-in fade-in zoom-in relative mx-4 w-full max-w-2xl duration-300">
        <div className="rounded-lg border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <span
                className={`rounded-md border px-3 py-1 text-sm font-semibold ${getLabelColor(transaction.label)}`}
              >
                {formatLabel(transaction.label)}
              </span>
              <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">
                {transaction.reference_numbers.length} transaction
                {transaction.reference_numbers.length !== 1 ? 's' : ''}
              </span>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>

          <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
            <div className="space-y-6">
              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900">Risk Indicator</h3>
                <p className="text-base leading-relaxed text-gray-700">{transaction.reason}</p>
              </div>

              {transaction.evidence.length > 0 && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-gray-900">Evidence</h3>
                  <ul className="space-y-2">
                    {transaction.evidence.map((item, index) => (
                      <li key={index} className="flex gap-2 text-sm text-gray-700">
                        <span className="text-gray-400">&bull;</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">Reference Numbers</h3>
                  <button
                    onClick={handleCopyReferences}
                    className="flex items-center gap-2 rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
                  >
                    {copied ? (
                      <>
                        <CheckCircle2 size={16} className="text-green-600" />
                        <span className="text-green-600">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy size={16} />
                        <span>Copy All</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 rounded-lg bg-gray-50 p-4 sm:grid-cols-3">
                  {transaction.reference_numbers.map((refNum, index) => (
                    <div
                      key={index}
                      className="rounded bg-white px-3 py-2 font-mono text-sm text-gray-700 shadow-sm"
                    >
                      {refNum}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 px-6 py-4">
            <button
              onClick={onClose}
              className="w-full rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
